"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelectField = exports.isOptionId = void 0;
const joi_1 = __importDefault(require("joi"));
const lodash_1 = require("lodash");
const utils_1 = require("../../../utils");
const field_1 = require("../field");
const field_types_1 = require("../../../types/field_types");
const color_1 = require("../../../model/color");
const validate_schema_1 = require("../validate_schema");
const EFFECTIVE_OPTION_ID_LENGTH = 13;
const isOptionId = (optionId) => {
    return optionId && optionId.startsWith('opt') && optionId.length === EFFECTIVE_OPTION_ID_LENGTH;
};
exports.isOptionId = isOptionId;
class SelectField extends field_1.Field {
    constructor(field, state) {
        super(field, state);
        this.field = field;
        this.getMapOption = (0, lodash_1.memoize)((options) => {
            const mapOptions = new Map();
            options.forEach((op, index) => {
                mapOptions.set(op.id, index);
            });
            return mapOptions;
        });
    }
    static defaultProperty() {
        return { options: [] };
    }
    get apiMetaProperty() {
        const options = this.field.property.options.map(option => {
            return {
                id: option.id,
                name: option.name,
                color: (0, color_1.getFieldOptionColor)(option.color),
            };
        });
        return { options };
    }
    validateProperty() {
        return SelectField.propertySchema.validate(this.field.property, { context: {
                fieldType: this.field.type,
            } });
    }
    static _createNewOption(option, existOptions) {
        const optionId = (0, utils_1.getNewId)(utils_1.IDPrefix.Option, existOptions.map(op => op.id));
        return {
            id: optionId,
            color: option.color || getOptionColor(existOptions.map(op => op.color)),
            name: option.name,
        };
    }
    static _getOption(option, existOptions) {
        for (const opt of existOptions) {
            const exist = option.color ? opt.name === option.name && opt.color === option.color : opt.name === option.name;
            if (exist) {
                return opt;
            }
        }
        return;
    }
    static getOrCreateNewOption(option, existOptions) {
        // get exist
        const opt = SelectField._getOption(option, existOptions);
        if (opt) {
            return {
                option: opt,
                isCreated: false,
            };
        }
        // create not exist
        const newOption = SelectField._createNewOption(option, existOptions);
        return {
            option: newOption,
            isCreated: true,
        };
    }
    createNewOption(name, color) {
        return SelectField._createNewOption({ name, color }, this.field.property.options);
    }
    /**
     * add new option to singleSelect
     *
     * @param {string} name
     * @memberof SingleSelectField
     */
    addOption(name, color) {
        const option = this.createNewOption(name, color);
        this.field.property.options.push(option);
        return option.id;
    }
    /**
     * find option by name
     * @param {string} name
     * @returns
     * @memberof SingleSelectField
     */
    findOptionByName(name) {
        return this.field.property.options.find(option => option.name === name) || null;
    }
    findOptionById(id) {
        return this.field.property.options.find(option => option.id === id) || null;
    }
    getOption(index) {
        return this.field.property.options[index];
    }
    compare(cellValue1, cellValue2) {
        if (cellValue1 === cellValue2) {
            return 0;
        }
        if (cellValue1 === null) {
            return 1;
        }
        if (cellValue2 === null) {
            return -1;
        }
        const arr1 = Array.isArray(cellValue1) ? cellValue1 : [cellValue1];
        const arr2 = Array.isArray(cellValue2) ? cellValue2 : [cellValue2];
        const maxLen = Math.max(arr1.length, arr2.length);
        const mapOptions = this.getMapOption(this.field.property.options);
        for (let i = 0; i < maxLen; i++) {
            if (!arr1[i]) {
                return -1;
            }
            if (!arr2[i]) {
                return 1;
            }
            const index1 = mapOptions.get(arr1[i]);
            const index2 = mapOptions.get(arr2[i]);
            if (index1 !== index2) {
                return index1 > index2 ? 1 : -1;
            }
        }
        return 0;
    }
    // Modify the current property according to StandardValue
    enrichProperty(stdVals) {
        if (!this.propertyEditable()) {
            // Filling non-existent values for single and multiple selection will be enriched by default, 
            // but if there is no manageable permission for the node, enrich will report an error
            // In addition, considering the column permissions, if there is no column editing permission, 
            // the user must not be manageable by the node, so there is no need to check the column permissions.
            return this.field.property;
        }
        // The two maps are because there are multiple options corresponding to one id
        const options = [...this.field.property.options];
        const optionColor = options.map(op => op.color);
        const optionNameMap = (0, lodash_1.keyBy)(this.field.property.options, 'name');
        const optionIdMap = (0, lodash_1.keyBy)(this.field.property.options, 'id');
        for (const stdVal of stdVals) {
            const sourceType = stdVal && stdVal.sourceType;
            const isSelect2Multi = (0, utils_1.isSelectType)(sourceType) &&
                this.field.type === field_types_1.FieldType.MultiSelect;
            const data = stdVal && stdVal.data;
            if (!data || data.length === 0) {
                continue;
            }
            data.forEach(d => {
                const { text, id } = d;
                let textList;
                if (isSelect2Multi) {
                    textList = [text];
                }
                else {
                    textList = this.field.type === field_types_1.FieldType.MultiSelect ? text.split(/, ?/) : [text];
                }
                textList.forEach(text => {
                    const existOption = optionNameMap[text];
                    // TODO: reuse ID and Color
                    // When there is a creation option, two cases with the same option have id, such as the color field added later
                    if ((0, lodash_1.isString)(text) && !existOption) {
                        const newColor = getOptionColor(optionColor);
                        const option = this.createNewOption(text, newColor);
                        if (id) {
                            // Create one more option, there may be duplicate text
                            // Subsequent reuse of color, such as option.color = color
                            option.id = id;
                        }
                        options.push(option);
                        optionNameMap[text] = option;
                        optionIdMap[id] = option;
                        optionColor.push(newColor);
                    }
                });
            });
        }
        // Keep the property of the current column, options are overwritten
        return Object.assign(Object.assign({}, this.field.property), { options: this.filterBlankOption(options) });
    }
    filterBlankOption(options) {
        return options.filter(item => item.name.trim().length);
    }
    get openFieldProperty() {
        const { defaultValue } = this.field.property;
        const options = this.field.property.options.map(option => {
            return {
                id: option.id,
                name: option.name,
                color: (0, color_1.getFieldOptionColor)(option.color),
            };
        });
        return { options, defaultValue };
    }
    validateWriteOpenOptionsEffect(updateProperty, effectOption) {
        // Not allowed to pass option parameter with ID but no color
        if (updateProperty.options.some(option => option.id && !option.color)) {
            return (0, validate_schema_1.joiErrorResult)('Option object is not supported. It has id but no color');
        }
        // Check if this update removes options
        const updateOptionIds = updateProperty.options.map(option => option.id);
        const isDeleteOption = this.field.property.options.some(option => !updateOptionIds.includes(option.id));
        if (isDeleteOption && !(effectOption === null || effectOption === void 0 ? void 0 : effectOption.enableSelectOptionDelete)) {
            return (0, validate_schema_1.joiErrorResult)('Removing options is not supported by default, '
                + 'When updating property, include all existing options or pass the `enableSelectOptionDelete` option to allow options to be deleted.');
        }
        return { error: undefined, value: undefined };
    }
    updateOpenFieldPropertyTransformProperty(openFieldProperty) {
        const { options, defaultValue } = openFieldProperty;
        const newOptions = [];
        let transformedDefaultValue = defaultValue;
        const transformedOptions = options.map(option => {
            if (!option.id || !option.color) {
                const color = option.color ? this.getOptionColorNumberByName(option.color) : undefined;
                // prevent duplicate option IDs
                const newOption = SelectField._createNewOption({ name: option.name, color }, [...this.field.property.options, ...newOptions]);
                transformedDefaultValue = this.transformDefaultValue(newOption, transformedDefaultValue);
                newOptions.push(newOption);
                return newOption;
            }
            return {
                id: option.id,
                name: option.name,
                color: this.getOptionColorNumberByName(option.color)
            };
        });
        return {
            defaultValue: transformedDefaultValue,
            options: transformedOptions
        };
    }
    /**
     * If the defaultValue is option.name, it needs to be processed as option.id
     */
    transformDefaultValue(option, defaultValue) {
        if (this.matchSingleSelectName(option.name, defaultValue)) {
            return option.id;
        }
        if (typeof defaultValue === 'object') { // for MultiSelect
            const idx = defaultValue.indexOf(option.name);
            if (idx > -1) {
                defaultValue[idx] = option.id;
            }
        }
        return defaultValue;
    }
    matchSingleSelectName(name, defaultValue) {
        return typeof defaultValue === 'string'
            && name === defaultValue;
    }
    /**
     * Convert the obtained color name to color number
     * @param name color name
     */
    getOptionColorNumberByName(name) {
        const colorNames = (0, color_1.getColorNames)();
        const colorNum = colorNames.findIndex(colorName => colorName === name);
        return colorNum > -1 ? colorNum : undefined;
    }
}
exports.SelectField = SelectField;
SelectField.propertySchema = joi_1.default.object({
    options: joi_1.default.array().items(joi_1.default.object({
        id: joi_1.default.string().required(),
        name: joi_1.default.string().required(),
        color: joi_1.default.number().integer().min(0).required(),
    })).required(),
    defaultValue: joi_1.default.custom((_prototype, helpers) => {
        var _a;
        if (((_a = helpers.prefs['context']) === null || _a === void 0 ? void 0 : _a['fieldType']) === field_types_1.FieldType.SingleSelect) {
            return joi_1.default.string();
        }
        return joi_1.default.array().items(joi_1.default.string());
    })
}).required();
SelectField.openUpdatePropertySchema = joi_1.default.object({
    options: joi_1.default.array().items(joi_1.default.object({
        id: joi_1.default.string(),
        name: joi_1.default.string().required(),
        color: joi_1.default.string(),
    })).required(),
}).required();
// TODO: wait for PRD for specific logic
function getOptionColor(colors) {
    if (colors.length < 10) {
        const diffColors = (0, lodash_1.difference)((0, lodash_1.range)(10), colors);
        return diffColors[0];
    }
    return colors.length % 10;
}
//# sourceMappingURL=common_select_field.js.map